/**
 * Boot may activate a waiting service worker only when the page has nothing
 * the user would lose. An open dialog, a dirty field, or a save in flight
 * keeps the update bar instead.
 */

let pendingMutations = 0;

/** Call the returned function when the mutation settles. */
export function beginPendingMutation(): () => void {
  pendingMutations += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pendingMutations = Math.max(0, pendingMutations - 1);
  };
}

export function pendingMutationCount(): number {
  return pendingMutations;
}

function controlIsDirty(control: Element): boolean {
  if (control instanceof HTMLInputElement) {
    if (
      control.type === "hidden" ||
      control.type === "button" ||
      control.type === "submit" ||
      control.type === "file" ||
      control.type === "reset"
    ) {
      return false;
    }
    if (control.type === "checkbox" || control.type === "radio") {
      return control.checked !== control.defaultChecked;
    }
    return control.value !== control.defaultValue;
  }
  if (control instanceof HTMLTextAreaElement) {
    return control.value !== control.defaultValue;
  }
  if (control instanceof HTMLSelectElement) {
    return Array.from(control.options).some(
      (option) => option.selected !== option.defaultSelected,
    );
  }
  return false;
}

export function hasUnsavedWork(root: ParentNode = document): boolean {
  if (pendingMutations > 0) return true;
  if (root.querySelector("[role='dialog'][data-state='open']")) return true;
  for (const control of root.querySelectorAll("input, textarea, select")) {
    const dialog = control.closest("[role='dialog']");
    if (dialog && dialog.getAttribute("data-state") !== "open") continue;
    if (controlIsDirty(control)) return true;
  }
  return false;
}
